import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { apiGet, apiPost } from '../lib/api';

interface Client {
  id: string;
  stripe_customer_id: string;
  display_name: string;
  display_email: string | null;
  tags: string[];
  default_markup_pct: number;
  cached_at: string;
  created_at: string;
}

export function ClientsPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formMarkup, setFormMarkup] = useState('30');
  const [formNotes, setFormNotes] = useState('');
  const [formAddressLine1, setFormAddressLine1] = useState('');
  const [formAddressLine2, setFormAddressLine2] = useState('');
  const [formAddressCity, setFormAddressCity] = useState('');
  const [formAddressState, setFormAddressState] = useState('');
  const [formAddressPostalCode, setFormAddressPostalCode] = useState('');
  const [formAddressCountry, setFormAddressCountry] = useState('');
  const [formTaxId, setFormTaxId] = useState('');
  const [formTaxIdType, setFormTaxIdType] = useState('other');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    void loadClients();
  }, []);

  async function loadClients(): Promise<void> {
    try {
      const data = await apiGet<Client[]>('/api/clients');
      setClients(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load clients');
    }
  }

  async function syncAll(): Promise<void> {
    setSyncingAll(true);
    setError(null);
    try {
      const result = await apiPost<{ synced_count: number; total: number }>('/api/clients/sync-all', {}, { successMessage: 'All clients synced from Stripe' });
      if (result.synced_count < result.total) {
        setError(`Synced ${result.synced_count}/${result.total} clients. Some failed.`);
      }
      await loadClients();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync clients');
    } finally {
      setSyncingAll(false);
    }
  }

  async function syncOne(clientId: string): Promise<void> {
    setSyncingId(clientId);
    try {
      await apiPost(`/api/clients/${clientId}/sync`, {}, { successMessage: 'Client synced' });
      await loadClients();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sync client');
    } finally {
      setSyncingId(null);
    }
  }

  async function createClient(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!formName.trim() || !formEmail.trim()) {
      setError('Name and email are required');
      return;
    }
    const markupPct = Number(formMarkup) / 100;
    if (Number.isNaN(markupPct) || markupPct < 0 || markupPct > 1) {
      setError('Markup must be between 0 and 100');
      return;
    }

    setCreating(true);
    setError(null);
    try {
      const tags = formTags.split(',').map((t) => t.trim()).filter(Boolean);
      await apiPost('/api/clients', {
        name: formName.trim(),
        email: formEmail.trim(),
        phone: formPhone.trim() || undefined,
        tags: tags.length > 0 ? tags : undefined,
        default_markup_pct: markupPct,
        notes: formNotes.trim() || undefined,
        address_line1: formAddressLine1.trim() || undefined,
        address_line2: formAddressLine2.trim() || undefined,
        address_city: formAddressCity.trim() || undefined,
        address_state: formAddressState.trim() || undefined,
        address_postal_code: formAddressPostalCode.trim() || undefined,
        address_country: formAddressCountry.trim().toUpperCase() || undefined,
        tax_id: formTaxId.trim() || undefined,
        tax_id_type: formTaxId.trim() ? formTaxIdType : undefined,
      }, { successMessage: 'Client created' });
      setFormName('');
      setFormEmail('');
      setFormPhone('');
      setFormTags('');
      setFormMarkup('30');
      setFormNotes('');
      setFormAddressLine1('');
      setFormAddressLine2('');
      setFormAddressCity('');
      setFormAddressState('');
      setFormAddressPostalCode('');
      setFormAddressCountry('');
      setFormTaxId('');
      setFormTaxIdType('other');
      setShowForm(false);
      await loadClients();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Clients</h1>
          <p className="mt-1 text-sm text-slate-600">Manage client relationships. Data synced from Stripe.</p>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => { void syncAll(); }} disabled={syncingAll} className="rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-50">
            {syncingAll ? 'Syncing...' : 'Sync All from Stripe'}
          </button>
          <button type="button" onClick={() => setShowForm((p) => !p)} className="rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700">
            {showForm ? 'Cancel' : 'Add Client'}
          </button>
        </div>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}

      {showForm ? (
        <form onSubmit={createClient} className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
          <h3 className="text-sm font-semibold text-slate-800">New Client</h3>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm font-medium">
              Name *
              <input value={formName} onChange={(e) => setFormName(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" required />
            </label>
            <label className="text-sm font-medium">
              Email *
              <input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" required />
            </label>
            <label className="text-sm font-medium">
              Phone
              <input value={formPhone} onChange={(e) => setFormPhone(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm font-medium">
              Default Markup % (0–100)
              <input type="number" min="0" max="100" step="1" value={formMarkup} onChange={(e) => setFormMarkup(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm font-medium">
              Tags (comma-separated)
              <input value={formTags} onChange={(e) => setFormTags(e.target.value)} placeholder="e.g. enterprise, hk" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm font-medium">
              Notes
              <textarea value={formNotes} onChange={(e) => setFormNotes(e.target.value)} rows={2} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
          </div>

          <details className="rounded-md border border-slate-200 bg-white p-3">
            <summary className="cursor-pointer text-sm font-medium text-slate-700">Address (optional)</summary>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <label className="text-sm font-medium md:col-span-2">
                Address Line 1
                <input value={formAddressLine1} onChange={(e) => setFormAddressLine1(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              </label>
              <label className="text-sm font-medium md:col-span-2">
                Address Line 2
                <input value={formAddressLine2} onChange={(e) => setFormAddressLine2(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              </label>
              <label className="text-sm font-medium">
                City
                <input value={formAddressCity} onChange={(e) => setFormAddressCity(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              </label>
              <label className="text-sm font-medium">
                State
                <input value={formAddressState} onChange={(e) => setFormAddressState(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              </label>
              <label className="text-sm font-medium">
                Postal Code
                <input value={formAddressPostalCode} onChange={(e) => setFormAddressPostalCode(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
              </label>
              <label className="text-sm font-medium">
                Country (2-letter ISO)
                <input value={formAddressCountry} onChange={(e) => setFormAddressCountry(e.target.value)} maxLength={2} placeholder="HK" className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm uppercase" />
              </label>
            </div>
          </details>

          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm font-medium">
              Tax ID
              <input value={formTaxId} onChange={(e) => setFormTaxId(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
            </label>
            <label className="text-sm font-medium">
              Tax ID Type
              <select value={formTaxIdType} onChange={(e) => setFormTaxIdType(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm">
                <option value="hk_br">hk_br</option>
                <option value="us_ein">us_ein</option>
                <option value="au_abn">au_abn</option>
                <option value="cn_tin">cn_tin</option>
                <option value="eu_vat">eu_vat</option>
                <option value="gb_vat">gb_vat</option>
                <option value="sg_uen">sg_uen</option>
                <option value="other">other</option>
              </select>
            </label>
          </div>
          <button type="submit" disabled={creating} className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-60">
            {creating ? 'Creating...' : 'Create Client'}
          </button>
        </form>
      ) : null}

      <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Tags</th>
              <th className="px-3 py-2">Markup</th>
              <th className="px-3 py-2">Last Synced</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((client) => (
              <tr key={client.id} className="border-b border-slate-100 last:border-0">
                <td className="px-3 py-2">
                  <Link to={`/clients/${client.id}`} className="font-medium text-brand-700 hover:underline">{client.display_name}</Link>
                </td>
                <td className="px-3 py-2 text-slate-600">{client.display_email ?? '-'}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {client.tags.length > 0 ? client.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">{tag}</span>
                    )) : <span className="text-slate-400">-</span>}
                  </div>
                </td>
                <td className="px-3 py-2">{Math.round(client.default_markup_pct * 100)}%</td>
                <td className="px-3 py-2 text-xs text-slate-500">{shortDate(client.cached_at)}</td>
                <td className="px-3 py-2">
                  <button type="button" onClick={() => { void syncOne(client.id); }} disabled={syncingId === client.id} className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50">
                    {syncingId === client.id ? 'Syncing...' : 'Sync'}
                  </button>
                </td>
              </tr>
            ))}
            {clients.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-500">No clients yet. Add one or sync from Stripe.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function shortDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}
